import { time } from "./time.js";
import { descriptors } from "./descriptors.js";
import { validation } from "./validation.js";
import { easingFormulas } from "../utils/easing-formulas.js";
import { encrypt } from "../utils/encrypt.js";
import { events } from "./events.js";

function validateTransformEntry(entry, namespace) {
  const descriptor = descriptors.get(namespace);
  if (!descriptor) return true;

  const propDescriptor = descriptor.properties[entry.property];
  if (!propDescriptor) return true;

  const context = { namespace };

  if (entry.valueEnd !== undefined) {
    const result = validation.validateProperty(entry.valueEnd, propDescriptor, false, context);
    if (!result.isValid) return false;
  }
  if (entry.valueStart !== undefined) {
    const result = validation.validateProperty(entry.valueStart, propDescriptor, false, context);
    if (!result.isValid) return false;
  }

  return true;
}

function prepareTransform(config, namespace) {
  if (config.transforms) {
    const groupDelay = config.delay || 0;
    const mode = config.mode || "parallel";

    config.transforms = config.transforms.filter(entry => validateTransformEntry(entry, namespace));
    if (config.transforms.length === 0) return config;

    if (mode === "sequence") {
      let cumulativeOffset = groupDelay;
      for (const entry of config.transforms) {
        entry.delay = cumulativeOffset + (entry.delay || 0);
        cumulativeOffset = entry.delay + entry.duration;
      }
    } else {
      for (const entry of config.transforms) {
        entry.delay = groupDelay + (entry.delay || 0);
      }
    }

    return config;
  }

  if (config.property) {
    if (!validateTransformEntry(config, namespace)) {
      config._invalid = true;
    }
  }

  return config;
}

function startTransform(node, config) {
  if (config.transforms) {
    const groupId = encrypt.generateUid();
    for (const entry of config.transforms) {
      node._transforms[entry.property] = {
        ...entry,
        startTime: null,
        group: groupId,
      };
    }

    node._groups ??= {};
    node._groups[groupId] = {
      loop: config.loop || false,
      event: config.event || null,
      mode: config.mode || "parallel",
      active: config.transforms.length,
      entries: config.transforms,
    };

    transformRegistry.register(node);
    return;
  }

  if (!config.property || config._invalid) return;

  node._transforms[config.property] = {
    ...config,
    startTime: null,
  };

  transformRegistry.register(node);
}

function tickTransforms(node) {
  for (const property of Object.keys(node._transforms)) {
    const tween = node._transforms[property];

    if (tween.startTime === null) {
      if (tween.valueStart === undefined) {
        const current = node.props[property];
        tween.valueStart = current !== undefined ? current : 0;
      }
      tween.ease = easingFormulas[tween.easing].bind(easingFormulas);
      tween.range = tween.valueEnd - tween.valueStart;
      if (isNaN(tween.range)) {
        delete node._transforms[property];
        continue;
      }
      tween.startTime = time.current + (tween.delay || 0);
    }

    if (time.current < tween.startTime) {
      continue;
    }

    const elapsed = time.current - tween.startTime;
    const normalized = tween.duration === 0 ? 1 : Math.min(elapsed / tween.duration, 1);
    const interpolated = tween.valueStart + tween.range * tween.ease(normalized, tween.curve);

    node.coreImplementation.update(node, { key: property, value: interpolated, sender: "transform" });

    if (normalized === 1) {
      const groupId = tween.group;
      delete node._transforms[property];

      if (groupId && node._groups?.[groupId]) {
        const group = node._groups[groupId];
        group.active--;

        if (group.active === 0) {
          if (group.loop === true) {
            for (const entry of group.entries) {
              node._transforms[entry.property] = {
                ...entry,
                valueStart: undefined,
                startTime: null,
                group: groupId,
              };
            }
            group.active = group.entries.length;
          } else {
            if (group.event) {
              events.dispatch(group.event);
            }
            delete node._groups[groupId];
          }
        }
      }
    }
  }
}

function stopTransforms(node) {
  if (node._transforms && Object.keys(node._transforms).length > 0) {
    node._transforms = {};
    transformRegistry.unregister(node);
  }
  if (node._groups) {
    node._groups = {};
  }
}

class TransformRegistry {
  _nodes = new Set();
  _running = false;

  register(node) {
    this._nodes.add(node);
    if (!this._running) {
      this._running = true;
      time.preUpdate(this._tick.bind(this));
    }
  }

  unregister(node) {
    this._nodes.delete(node);
  }

  _tick() {
    for (const node of this._nodes) {
      tickTransforms(node);
      if (Object.keys(node._transforms).length === 0) {
        this.unregister(node);
      }
    }
    if (this._nodes.size === 0) {
      this._running = false;
    } else {
      time.nextFrame(this._tick.bind(this));
    }
  }
}

const transformRegistry = new TransformRegistry();
export { TransformRegistry, transformRegistry, prepareTransform, startTransform, stopTransforms };
